import React, { useState, useEffect } from 'react';
import {
    Paper,
    Button,
    Typography,
    Stack,
    TextField,
    Alert,
    CircularProgress,
    Box,
    Divider,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Grid,
    Snackbar
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import userService from 'api/userService';
import { ToastContainer, toast } from 'react-toastify';

export default function CustomerManagementMVP({ customerId, onUpdate, initialData }) {
    const queryClient = useQueryClient();

    // Form States
    const [profile, setProfile] = useState({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        phoneNumber: initialData.phoneNumber || '',
        status: initialData.status || 'active', // active, inactive, suspended
        isBanned: initialData.isBanned || false,
        ninStatus: initialData.ninStatus || 'not_submitted'
    });
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [reasonForBanned, setReasonForBanned] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        setProfile({
            firstName: initialData.firstName || '',
            lastName: initialData.lastName || '',
            phoneNumber: initialData.phoneNumber || '',
            status: initialData.status || 'active',
            isBanned: initialData.isBanned || false,
            ninStatus: initialData.ninStatus || 'not_submitted'
        });
        // Crucial: Clear the reason so it doesn't get stuck in the text field
        setReasonForBanned('');
    }, [initialData]);

    // 🔹 Combined Mutation for Profile & Status
    const updateCustomerMutation = useMutation({
        mutationFn: (payload) => userService.adminUpdateUser(customerId, payload),
        onSuccess: async (res) => {
            if (res.success) {
                // 1. Tell React Query to refetch the data from the server
                await queryClient.invalidateQueries({
                    queryKey: ['adminCustomerDetails', customerId]
                });

                // 2. Trigger the parent's remount
                onUpdate(res.data);

                // 3. UI Feedback
                setOpenSnackbar(true);
                setError('');

                // 4. Send the NEW data back up to the parent
                // Assuming res.data is the updated customer object
                if (onUpdate) onUpdate(res.data);

                setOpenSnackbar(true);
                // toast.success('Customer updated successfully!');
            }
        },
        onError: (err) => {
            // toast.error(err?.response?.data?.message || 'Update failed');
            setError(err?.response?.data?.message || 'Update failed');
        }
    });

    const handleSave = () => {
        // Validation: If status is suspended or isBanned is true, require a reason
        if ((profile.status === 'suspended' || profile.isBanned) && !reasonForBanned) {
            setError('Please provide a reason for suspension or banning.');
            return;
        }

        updateCustomerMutation.mutate({
            ...profile,
            reasonForBanned: reasonForBanned // Sending the reason to the backend
        });
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 3, background: 'primaryLight' }}>
            <ToastContainer />
            <Typography variant="h6" fontWeight={600} mb={3}>
                Account Settings & Restrictions
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Stack spacing={3}>
                {/* 1. Basic Info */}
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                        Identity Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={4} md={12}>
                            <TextField
                                label="First Name"
                                fullWidth
                                size="small"
                                value={profile.firstName}
                                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4} md={12}>
                            <TextField
                                label="Last Name"
                                fullWidth
                                size="small"
                                value={profile.lastName}
                                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4} md={12}>
                            <TextField
                                label="Phone Number"
                                fullWidth
                                size="small"
                                value={profile.phoneNumber}
                                onChange={(e) => setProfile({ ...profile, phoneNumber: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider />

                {/* 2. Status & Ban Logic */}
                <Box>
                    {' '}
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                        Account Lifecycle
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControl size="small" sx={{ width: 160 }}>
                                <InputLabel>Account Status</InputLabel>
                                <Select
                                    value={profile.status}
                                    label="Account Status"
                                    onChange={(e) => setProfile({ ...profile, status: e.target.value })}
                                >
                                    <MenuItem value="active">🟢 Active</MenuItem>
                                    <MenuItem value="inactive">⚪ Inactive</MenuItem>
                                    {/* <MenuItem value="suspended">🟡 Suspended</MenuItem> */}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl size="small" sx={{ width: 160 }}>
                                <InputLabel>NIN Verification</InputLabel>
                                <Select
                                    value={profile.ninStatus}
                                    label="NIN Verification"
                                    onChange={(e) => setProfile({ ...profile, ninStatus: e.target.value })}
                                >
                                    <MenuItem value="not_submitted">⚪ Not Submitted</MenuItem>
                                    <MenuItem value="pending">🟡 Pending</MenuItem>
                                    <MenuItem value="verified">🟢 Verified</MenuItem>
                                    <MenuItem value="failed">🔴 Failed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={profile.isBanned}
                                        color="error"
                                        onChange={(e) => setProfile({ ...profile, isBanned: e.target.checked })}
                                    />
                                }
                                label={<Typography color={profile.isBanned ? 'error' : 'inherit'}>Banned (Blacklisted)</Typography>}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* 3. Reason Field (Contextual) */}
                {(profile.status === 'suspended' || profile.isBanned) && (
                    <TextField
                        label="Reason for restriction"
                        placeholder="Why is this account being suspended/banned?"
                        multiline
                        rows={2}
                        fullWidth
                        value={reasonForBanned}
                        onChange={(e) => setReasonForBanned(e.target.value)}
                        required
                    />
                )}
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={updateCustomerMutation.isPending}
                    startIcon={updateCustomerMutation.isPending && <CircularProgress size={16} color="inherit" />}
                    sx={{ mt: 3, py: 1.2, fontWeight: 600 }}
                >
                    {updateCustomerMutation.isPending ? 'Updating...' : 'Update Account Details'}
                </Button>

                {/* Success Snackbar */}
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={4000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
                        Customer details refreshed and updated!
                    </Alert>
                </Snackbar>
            </Stack>
        </Paper>
    );
}
